// features/inventory/pages/SettingsPage.tsx
/**
 * Inventory Settings Page — Branch-level inventory configuration.
 *
 * Route: /inventory/settings
 *
 * Features:
 * - Branch selection for multi-store setups
 * - Auto-confirm stock toggle with animated switch
 * - Require physical count toggle (conditional on auto-confirm OFF)
 * - Stock flow visualization: Pending → Counted → Confirmed → Sellable
 * - Confirmation status progress bar
 * - Confirm all pending button
 * - Optimistic UI updates
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getBranchStockSettings,
    updateBranchStockSettings,
    BranchStockSettings,
} from '../services/inventoryApi';
import {
    getStockConfirmationStatus,
    confirmStock,
    ConfirmationStatus,
} from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft,
    Save,
    Store,
    CheckCircle,
    AlertTriangle,
    Info,
    Shield,
    Clock,
    Package,
    ClipboardCheck,
    ShieldCheck,
    RefreshCw,
    ChevronRight,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

const STOCK_FLOW_STATES = [
    {
        label: 'Pending',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: Clock,
    },
    {
        label: 'Counted',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: ClipboardCheck,
    },
    {
        label: 'Confirmed',
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        icon: CheckCircle,
    },
    {
        label: 'Sellable',
        color: 'bg-green-100 text-green-700 border-green-200',
        icon: Package,
    },
];

// ============================================================
// SUB-COMPONENTS
// ============================================================

function StatBox({
    label,
    value,
    color,
    textColor = 'text-slate-900',
}: {
    label: string;
    value: number;
    color: string;
    textColor?: string;
}) {
    return (
        <div className={clsx('rounded-xl p-4 text-center', color)}>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                {label}
            </p>
            <p className={clsx('text-2xl font-bold mt-1', textColor)}>
                {value.toLocaleString()}
            </p>
        </div>
    );
}

function ToggleSwitch({
    checked,
    onChange,
    disabled = false,
}: {
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            onClick={onChange}
            disabled={disabled}
            className={clsx(
                'relative w-14 h-8 rounded-full transition-colors duration-300 flex-shrink-0',
                checked ? 'bg-emerald-500' : 'bg-slate-300',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
            type="button"
            role="switch"
            aria-checked={checked}
        >
            <motion.div
                className="absolute top-0.5 w-7 h-7 bg-white rounded-full shadow-md"
                animate={{
                    left: checked
                        ? 'calc(100% - 1.875rem)'
                        : '0.125rem',
                }}
                transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30,
                }}
            />
        </button>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SettingsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [selectedBranchId, setSelectedBranchId] =
        useState<string>(NIL_UUID);
    const [autoConfirm, setAutoConfirm] = useState(true);
    const [requirePhysicalCount, setRequirePhysicalCount] =
        useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-settings', user?.tenantId],
        queryFn: async () => {
            const data = await getBranches(user!.tenantId);
            return Array.isArray(data) ? data : [];
        },
        enabled: !!user?.tenantId,
    });

    const {
        data: settings,
        isLoading: settingsLoading,
    } = useQuery({
        queryKey: [
            'branch-settings',
            user?.tenantId,
            selectedBranchId,
        ],
        queryFn: () =>
            getBranchStockSettings(
                user!.tenantId,
                selectedBranchId || NIL_UUID
            ),
        enabled: !!user?.tenantId,
    });

    const {
        data: confirmStatus,
        isLoading: statusLoading,
        refetch: refetchStatus,
    } = useQuery({
        queryKey: [
            'confirmation-status',
            user?.tenantId,
            selectedBranchId,
        ],
        queryFn: () =>
            getStockConfirmationStatus(
                user!.tenantId,
                selectedBranchId || NIL_UUID
            ),
        enabled: !!user?.tenantId,
    });

    // Sync settings when data loads
    useEffect(() => {
        if (settings) {
            setAutoConfirm(settings.autoConfirmStock);
            setRequirePhysicalCount(
                settings.requirePhysicalCount
            );
            setHasChanges(false);
        }
    }, [settings]);

    // Mutations
    const saveSettingsMutation = useMutation({
        mutationFn: () =>
            updateBranchStockSettings(
                user!.tenantId,
                selectedBranchId || NIL_UUID,
                {
                    autoConfirmStock: autoConfirm,
                    requirePhysicalCount,
                }
            ),
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Settings saved',
                message:
                    'Branch stock settings updated.',
            });
            setHasChanges(false);
            queryClient.invalidateQueries({
                queryKey: ['branch-settings'],
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to save',
                message: err?.message,
            });
        },
    });

    const confirmAllMutation = useMutation({
        mutationFn: () =>
            confirmStock(
                user!.tenantId,
                selectedBranchId || NIL_UUID
            ),
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Stock confirmed',
                message:
                    'All pending stock has been confirmed as sellable.',
            });
            refetchStatus();
            queryClient.invalidateQueries({
                queryKey: ['inventory'],
            });
            queryClient.invalidateQueries({
                queryKey: ['confirm-status-dash'],
            });
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Confirmation failed',
                message: err?.message,
            }),
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        code?: string;
    }>;
    const isSingleStore = branchesList.length === 0;

    const getBranchName = useCallback(
        (id: string) => {
            if (id === NIL_UUID || !id) return 'Warehouse';
            return (
                branchesList.find((b: any) => b.id === id)
                    ?.name || 'Warehouse'
            );
        },
        [branchesList]
    );

    const confirmedPercent =
        confirmStatus && confirmStatus.total > 0
            ? Math.round(
                (confirmStatus.confirmed /
                    confirmStatus.total) *
                100
            )
            : 0;
    const countedPercent =
        confirmStatus && confirmStatus.total > 0
            ? Math.round(
                (confirmStatus.counted /
                    confirmStatus.total) *
                100
            )
            : 0;
    const pendingPercent =
        confirmStatus && confirmStatus.total > 0
            ? Math.round(
                (confirmStatus.pending /
                    confirmStatus.total) *
                100
            )
            : 0;

    // Handlers
    const handleAutoConfirmToggle = useCallback(() => {
        setAutoConfirm(!autoConfirm);
        setHasChanges(true);
        if (!autoConfirm) {
            setRequirePhysicalCount(false);
        }
    }, [autoConfirm]);

    const handleRequirePhysicalCountToggle = useCallback(() => {
        setRequirePhysicalCount(!requirePhysicalCount);
        setHasChanges(true);
    }, [requirePhysicalCount]);

    const handleSave = useCallback(() => {
        saveSettingsMutation.mutate();
    }, [saveSettingsMutation]);

    const handleConfirmAll = useCallback(() => {
        if (
            window.confirm(
                `Confirm all ${confirmStatus?.pending || 0} pending items? They will be marked as sellable.`
            )
        ) {
            confirmAllMutation.mutate();
        }
    }, [confirmAllMutation, confirmStatus]);

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/25">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Inventory Settings
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {isSingleStore
                                        ? 'Warehouse'
                                        : getBranchName(
                                            selectedBranchId
                                        )}
                                    {confirmStatus &&
                                        ` · ${confirmStatus.confirmed} confirmed`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() =>
                                    refetchStatus()
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw className="w-4 h-4" />{' '}
                                Refresh
                            </button>
                            {hasChanges && (
                                <button
                                    onClick={handleSave}
                                    disabled={
                                        saveSettingsMutation.isPending
                                    }
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg text-sm"
                                    type="button"
                                >
                                    {saveSettingsMutation.isPending ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    Save Changes
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1200px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Branch Selection */}
                {!isSingleStore &&
                    branchesList.length > 0 && (
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 ring-1 ring-blue-200/50">
                                        <Store className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-800">
                                            Select Branch
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Configure settings
                                            per branch
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {branchesList.map(
                                        (b: any) => (
                                            <button
                                                key={b.id}
                                                onClick={() => {
                                                    setSelectedBranchId(
                                                        b.id
                                                    );
                                                    setHasChanges(
                                                        false
                                                    );
                                                }}
                                                className={clsx(
                                                    'p-4 rounded-xl border-2 text-left transition-all hover:shadow-md',
                                                    selectedBranchId ===
                                                        b.id
                                                        ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-500/20'
                                                        : 'border-slate-200 hover:border-blue-300'
                                                )}
                                                type="button"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div
                                                            className={clsx(
                                                                'p-2 rounded-lg',
                                                                selectedBranchId ===
                                                                    b.id
                                                                    ? 'bg-blue-100'
                                                                    : 'bg-slate-100'
                                                            )}
                                                        >
                                                            <Store
                                                                className={clsx(
                                                                    'w-5 h-5',
                                                                    selectedBranchId ===
                                                                        b.id
                                                                        ? 'text-blue-600'
                                                                        : 'text-slate-400'
                                                                )}
                                                            />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-sm">
                                                                {
                                                                    b.name
                                                                }
                                                            </p>
                                                            {b.code && (
                                                                <p className="text-xs text-slate-400">
                                                                    {
                                                                        b.code
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {selectedBranchId ===
                                                        b.id && (
                                                            <CheckCircle className="w-5 h-5 text-blue-500" />
                                                        )}
                                                </div>
                                            </button>
                                        )
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                {/* Confirmation Status */}
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 ring-1 ring-blue-200/50">
                                <ClipboardCheck className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-800">
                                    Stock Status
                                </h3>
                                <p className="text-xs text-slate-500">
                                    {getBranchName(
                                        selectedBranchId
                                    )}
                                </p>
                            </div>
                        </div>

                        {statusLoading ? (
                            <div className="flex justify-center py-8">
                                <Spinner size="lg" />
                            </div>
                        ) : confirmStatus ? (
                            <div className="space-y-5">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    <StatBox
                                        label="Total Items"
                                        value={
                                            confirmStatus.total
                                        }
                                        color="bg-slate-50"
                                    />
                                    <StatBox
                                        label="Pending"
                                        value={
                                            confirmStatus.pending
                                        }
                                        color="bg-amber-50"
                                        textColor="text-amber-700"
                                    />
                                    <StatBox
                                        label="Counted"
                                        value={
                                            confirmStatus.counted
                                        }
                                        color="bg-blue-50"
                                        textColor="text-blue-700"
                                    />
                                    <StatBox
                                        label="Confirmed"
                                        value={
                                            confirmStatus.confirmed
                                        }
                                        color="bg-emerald-50"
                                        textColor="text-emerald-700"
                                    />
                                </div>

                                {/* Progress Bar */}
                                <div className="space-y-3">
                                    <div className="w-full bg-slate-200 rounded-full h-5 overflow-hidden">
                                        <div className="flex h-full rounded-full overflow-hidden">
                                            <motion.div
                                                className="bg-emerald-500 h-full"
                                                initial={{
                                                    width: 0,
                                                }}
                                                animate={{
                                                    width: `${confirmedPercent}%`,
                                                }}
                                                transition={{
                                                    duration: 0.8,
                                                    ease: 'easeOut',
                                                }}
                                            />
                                            <motion.div
                                                className="bg-blue-500 h-full"
                                                initial={{
                                                    width: 0,
                                                }}
                                                animate={{
                                                    width: `${countedPercent}%`,
                                                }}
                                                transition={{
                                                    duration: 0.8,
                                                    delay: 0.2,
                                                    ease: 'easeOut',
                                                }}
                                            />
                                            <motion.div
                                                className="bg-amber-500 h-full"
                                                initial={{
                                                    width: 0,
                                                }}
                                                animate={{
                                                    width: `${pendingPercent}%`,
                                                }}
                                                transition={{
                                                    duration: 0.8,
                                                    delay: 0.4,
                                                    ease: 'easeOut',
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-[10px] font-medium flex-wrap">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />{' '}
                                            Confirmed (
                                            {
                                                confirmStatus.confirmed
                                            }
                                            )
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />{' '}
                                            Counted (
                                            {
                                                confirmStatus.counted
                                            }
                                            )
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />{' '}
                                            Pending (
                                            {
                                                confirmStatus.pending
                                            }
                                            )
                                        </span>
                                    </div>
                                </div>

                                {/* Confirm All Button */}
                                {confirmStatus.pending >
                                    0 && (
                                        <div className="flex justify-center">
                                            <button
                                                onClick={
                                                    handleConfirmAll
                                                }
                                                disabled={
                                                    confirmAllMutation.isPending
                                                }
                                                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all font-semibold text-sm shadow-lg shadow-amber-500/25 disabled:opacity-50"
                                                type="button"
                                            >
                                                {confirmAllMutation.isPending ? (
                                                    <Spinner
                                                        size="xs"
                                                        variant="white"
                                                    />
                                                ) : (
                                                    <CheckCircle className="w-4 h-4" />
                                                )}
                                                Confirm All
                                                Pending (
                                                {
                                                    confirmStatus.pending
                                                }
                                                )
                                            </button>
                                        </div>
                                    )}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                                    <Package className="w-8 h-8 text-slate-300" />
                                </div>
                                <p className="text-sm text-slate-500">
                                    No stock data available for
                                    this branch.
                                </p>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/inventory/counts/bulk'
                                        )
                                    }
                                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold text-sm shadow-lg"
                                    type="button"
                                >
                                    Upload Stock
                                </button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Settings */}
                <Card>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 ring-1 ring-blue-200/50">
                                <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-800">
                                    Confirmation Rules
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Configure how stock becomes
                                    sellable in{' '}
                                    {getBranchName(
                                        selectedBranchId
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Auto-Confirm Toggle */}
                        <div className="p-5 bg-slate-50 rounded-xl hover:bg-white transition-colors border border-transparent hover:border-slate-200">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="p-1.5 rounded-lg bg-emerald-100">
                                            <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                        </div>
                                        <h4 className="font-semibold text-sm">
                                            Auto-Confirm Stock
                                        </h4>
                                    </div>
                                    <p className="text-xs text-slate-500 ml-9">
                                        Stock is immediately
                                        sellable after upload
                                        or count. Best for
                                        single-store businesses
                                        with simple inventory.
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={autoConfirm}
                                    onChange={
                                        handleAutoConfirmToggle
                                    }
                                />
                            </div>
                        </div>

                        {/* Require Physical Count Toggle */}
                        <AnimatePresence>
                            {!autoConfirm && (
                                <motion.div
                                    initial={{
                                        opacity: 0,
                                        height: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        height: 'auto',
                                    }}
                                    exit={{
                                        opacity: 0,
                                        height: 0,
                                    }}
                                    className="overflow-hidden"
                                >
                                    <div className="p-5 bg-slate-50 rounded-xl hover:bg-white transition-colors border border-transparent hover:border-slate-200">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <div className="p-1.5 rounded-lg bg-blue-100">
                                                        <ClipboardCheck className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <h4 className="font-semibold text-sm">
                                                        Require
                                                        Physical
                                                        Count
                                                    </h4>
                                                </div>
                                                <p className="text-xs text-slate-500 ml-9">
                                                    Stock must
                                                    be
                                                    physically
                                                    counted
                                                    before it
                                                    can be
                                                    confirmed
                                                    as
                                                    sellable.
                                                    Recommended
                                                    for
                                                    multi-branch
                                                    operations.
                                                </p>
                                            </div>
                                            <ToggleSwitch
                                                checked={
                                                    requirePhysicalCount
                                                }
                                                onChange={
                                                    handleRequirePhysicalCountToggle
                                                }
                                            />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Stock Flow Visualization */}
                        <div className="p-5 bg-gradient-to-r from-slate-50 to-white rounded-xl border border-slate-200">
                            <h4 className="font-semibold text-sm mb-4">
                                Stock Flow in{' '}
                                {getBranchName(
                                    selectedBranchId
                                )}
                            </h4>
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                                {STOCK_FLOW_STATES.map(
                                    (state, idx) => (
                                        <div
                                            key={state.label}
                                            className="flex items-center gap-2"
                                        >
                                            <div
                                                className={clsx(
                                                    'flex items-center gap-1.5 px-3 py-2 rounded-lg border',
                                                    state.color
                                                )}
                                            >
                                                <state.icon className="w-3.5 h-3.5" />
                                                <span className="font-semibold">
                                                    {
                                                        state.label
                                                    }
                                                </span>
                                            </div>
                                            {idx < 3 && (
                                                <ChevronRight className="w-4 h-4 text-slate-300" />
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                            {autoConfirm && (
                                <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-600 flex items-center gap-2">
                                    <Info className="w-3.5 h-3.5" />
                                    With auto-confirm enabled,
                                    stock skips directly from
                                    Pending to Confirmed.
                                </div>
                            )}
                        </div>

                        {/* Save Button */}
                        {hasChanges && (
                            <div className="flex justify-end">
                                <button
                                    onClick={handleSave}
                                    disabled={
                                        saveSettingsMutation.isPending
                                    }
                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25 disabled:opacity-50"
                                    type="button"
                                >
                                    {saveSettingsMutation.isPending ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    Save Settings
                                </button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}